import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { GetRangeHistoryQuery, GetRangeHistoryQueryVariables, QuestMetricType } from '../client/generated/graphql';
import { getRangeHistoryQuery } from '../client/queries/rangeHistory';
import { ListGoal, ListQuest } from '../client/types';
import { getTargetXP } from '../helpers/goal';
import { useCachedQuestsWithFallback } from './caching/quest';

export type WeeklyGoalStats = {
    xpForDay: number;
    targetForDay: number;
};

export const useWeeklyGoalProgress = (goals: ListGoal[], goalCompletion: string): Record<string, WeeklyGoalStats[]> | null => {
    const endDate = dayjs(goalCompletion);
    const startDate = endDate.subtract(1, 'week');
    const { data: historyData, loading } = useQuery<GetRangeHistoryQuery, GetRangeHistoryQueryVariables>(getRangeHistoryQuery, {
        variables: {
            start: startDate,
            end: endDate,
        },
    });

    const logs = historyData?.eventLogs ?? [];

    const questIdsFromLogs = useMemo(() => {
        const ids = new Set<string>();
        logs.forEach(log => {
            log.questMetricIncreased.forEach(metric => {
                if (metric.questId) ids.add(metric.questId);
            });
        });
        return Array.from(ids);
    }, [logs]);

    const { quests } = useCachedQuestsWithFallback(questIdsFromLogs);
    const questMap: Record<string, ListQuest> = Object.fromEntries(quests.map(q => [q.id, q] as [string, ListQuest]));

    const statsMap = useMemo(() => {
        const dayDates = Array.from({ length: 7 }, (_, i) => startDate.add(i, 'day'));
        const initialStats: Record<string, WeeklyGoalStats[]> = goals.reduce((acc, goal) => {
            acc[goal.id] = dayDates.map(date => ({
                xpForDay: 0,
                targetForDay: getTargetXP(goal, date.day()),
            }));
            return acc;
        }, {} as Record<string, WeeklyGoalStats[]>);

        const updates =
            historyData?.eventLogs.flatMap(log => {
                const logDate = dayjs(log.createdAt);
                const dayIndex = dayDates.findIndex(date => logDate.isSame(date, 'day'));
                if (dayIndex === -1) return [];
                return log.questMetricIncreased
                    .filter(inc => inc.questMetricType === QuestMetricType.Experience)
                    .map(inc => ({ inc, dayIndex }));
            }) ?? [];

        return updates.reduce((acc, { inc, dayIndex }) => {
            const quest = questMap[inc.questId];
            const goalId = quest?.goalId;
            if (quest && goalId && acc[goalId]) {
                acc[goalId][dayIndex].xpForDay += inc.addedValue;
            }
            return acc;
        }, initialStats);
    }, [historyData?.eventLogs, goals, goalCompletion]);

    if (loading || !historyData?.eventLogs) {
        return null;
    }

    return statsMap;
};
